<script setup lang="ts">
import type { BeltColor } from '@@/bits/belt_colors'
import { getBadgeBackgroundProps } from '@@/bits/belt_colors'
import { useSettingsBeltProgressEffectStore } from '@@/pinia/settings_belt_progress_effect'
import { useSmoothAnimation } from '@@/vuecomposables/use_smooth_animation'
import { useReducedMotionMode } from '@@/vuecomposables/useReducedMotionMode'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'

const {
  certificateToken = '',
  certificateQrCode = '',
  sizePreset = CertificateQRCodeSizePreset.Display,
  beltColor = 'white',
} = defineProps<{
  certificateToken: string
  certificateQrCode: string
  sizePreset: CertificateQRCodeSizePreset
  beltColor?: BeltColor
}>()

const qrCodeToken = computed(() => {
  return certificateToken ? `#${certificateToken}` : ''
})

const badgeBackgroundProps = computed(() => getBadgeBackgroundProps(beltColor))

// Use shared effect store for mouse tracking
const effectStore = useSettingsBeltProgressEffectStore()
const { isHovering, globalMouseX, globalMouseY } = storeToRefs(effectStore)

// Check if user prefers reduced motion for accessibility
const { isInReducedMotionMode } = useReducedMotionMode()

const containerRef = ref<HTMLElement | null>(null)

// Get container dimensions based on size preset
const containerWidth = computed(() => {
  return sizePreset === CertificateQRCodeSizePreset.Display ? QR_CODE_DISPLAY_WIDTH : QR_CODE_PRINT_WIDTH
})

const containerHeight = computed(() => {
  return sizePreset === CertificateQRCodeSizePreset.Display ? QR_CODE_DISPLAY_HEIGHT : QR_CODE_PRINT_HEIGHT
})

// #region Holographic effect

// Constants for QR code dimensions and effect calculations
const QR_CODE_DISPLAY_WIDTH = 56 // Display size width in pixels
const QR_CODE_DISPLAY_HEIGHT = 72 // Display size height in pixels
const QR_CODE_PRINT_WIDTH = 78 // Print size width in pixels
const QR_CODE_PRINT_HEIGHT = 100 // Print size height in pixels
const CENTER_PERCENTAGE = 50 // Center point in percentage coordinates (50% = center)
// Rotation angle: -3.1deg
const ROTATION_ANGLE = -3.1

// Holographic effect constants
const LINEAR_GRADIENT_ANGLE = 45 // Fixed diagonal angle for linear gradient streaks (45deg = diagonal)
const MIN_HOLO_OPACITY = 0 // Minimum opacity (completely transparent when far from center)
const MAX_HOLO_OPACITY = 0.4 // Maximum opacity for QR code (lower than badge to not obscure QR scanning)

// Animation constants for smooth gradient offset transitions
const ANIMATION_SNAP_THRESHOLD = 0.01 // Snap to target when difference is below this threshold (prevents infinite animation)
const ANIMATION_EASING_FACTOR = 0.15 // Easing factor for smooth interpolation (0.15 = smooth, 0.3 = faster, 0.05 = slower)

// Cache the bounding rect to avoid expensive getBoundingClientRect() calls on every mouse move
// Cache is computed on first mousemove (when hover starts) and invalidated on mouseleave
const cachedRect = ref<DOMRect | null>(null)

// Watch for hover state changes to manage cache lifecycle
watch(isHovering, (hovering) => {
  if (hovering) {
    // Cache rect on first mousemove when hover starts
    if (containerRef.value && !cachedRect.value) {
      cachedRect.value = containerRef.value.getBoundingClientRect()
    }
  } else {
    // Invalidate cache when mouse leaves
    cachedRect.value = null
  }
})

// Track page scroll to update cached bounding rect
const isPageScrolling = ref(false)

let ticking = false
const onScroll = () => {
  if (!ticking) {
    window.requestAnimationFrame(() => {
      isPageScrolling.value = !isPageScrolling.value // Toggle to trigger reactivity
      ticking = false
    })
    ticking = true
  }
}
onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})

// Everytime scroll ends
watch(isPageScrolling, (value, oldValue) => {
  if (oldValue === true && value === false) {
    if (containerRef.value) {
      cachedRect.value = containerRef.value.getBoundingClientRect()
    }
  }
})

/**
 * Transform mouse coordinates from screen space to element's local coordinate system
 * Accounts for the element's rotation by applying inverse rotation transformation
 * Returns local coordinates relative to element center
 */
const getLocalCoordinates = computed(() => {
  if (!cachedRect.value || !isHovering.value || !containerRef.value) return { localX: 0, localY: 0 }

  // Calculate element center in screen coordinates
  // For rotated elements, getBoundingClientRect() gives the axis-aligned bounding box
  // The center of this box should correspond to the rotation center (transform-origin: center)
  // However, due to the rotation, the bounding box is larger than the element
  // We calculate the center as the midpoint of the bounding rect, which should be correct
  const centerX = cachedRect.value.left + cachedRect.value.width / 2
  const centerY = cachedRect.value.top + cachedRect.value.height / 2

  // Get mouse position relative to element center (in screen coordinates)
  const dx = globalMouseX.value - centerX
  const dy = globalMouseY.value - centerY

  // Transform coordinates from screen space to element's local coordinate system
  // Rotate by -ROTATION_ANGLE to undo the CSS rotation (inverse transformation)
  const angleRad = (-ROTATION_ANGLE * Math.PI) / 180
  const cos = Math.cos(angleRad)
  const sin = Math.sin(angleRad)
  // Standard 2D rotation matrix: rotate point (dx, dy) by angleRad
  // This transforms screen coordinates to the element's local (unrotated) coordinate system
  const localX = dx * cos - dy * sin
  const localY = dx * sin + dy * cos

  return { localX, localY }
})

// Calculate gradient position for CSS (percentage) relative to QR code container
// Accounts for the 3.1deg rotation by transforming mouse coordinates to element's local coordinate system
// Uses cached rect for performance optimization
const gradientXPercent = computed(() => {
  if (!cachedRect.value || !isHovering.value) return CENTER_PERCENTAGE

  const { localX } = getLocalCoordinates.value

  // Convert to percentage using element's logical dimensions
  const x = ((localX + containerWidth.value / 2) / containerWidth.value) * 100

  // Clamp to valid range [0, 100]
  return Math.max(0, Math.min(100, x))
})

const gradientYPercent = computed(() => {
  if (!cachedRect.value || !isHovering.value) return CENTER_PERCENTAGE

  const { localY } = getLocalCoordinates.value

  // Convert to percentage using element's logical dimensions
  const y = ((localY + containerHeight.value / 2) / containerHeight.value) * 100

  // Clamp to valid range [0, 100]
  return Math.max(0, Math.min(100, y))
})

/**
 * Calculate gradient offset for diagonal linear gradient
 * Creates a shifting pattern that follows cursor movement along the 45° diagonal
 * Result: -50% (top-left) to +50% (bottom-right) offset
 */
const targetGradientOffset = computed(() => {
  if (!cachedRect.value || !isHovering.value) {
    // When not hovering, return 0 and let the smooth animation transition naturally
    return 0
  }

  // Normalize position relative to center (0-100% becomes -50 to +50)
  const normalizedX = gradientXPercent.value - CENTER_PERCENTAGE
  const normalizedY = gradientYPercent.value - CENTER_PERCENTAGE

  // Calculate diagonal position by subtracting y from x normalized positions
  // This creates movement along the -45° angle (perpendicular to the 45° linear gradient)
  const diagonalPosition = (normalizedX - normalizedY) / 2

  return diagonalPosition
})

// Smoothly interpolated offset for display using requestAnimationFrame
// Uses shared animation composable to handle easing, cleanup, and frame management
const gradientOffset = useSmoothAnimation(targetGradientOffset, {
  snapThreshold: ANIMATION_SNAP_THRESHOLD,
  easingFactor: ANIMATION_EASING_FACTOR,
})

// Calculate gradient initial offset percentage based on x/y position (convert 0-100 to -50 to 50)
const gradientInitialOffset = computed(() => {
  // Convert 0-100 range to -50 to 50
  const offset = gradientOffset.value - 50
  // Clamp to valid range for CSS gradient positioning
  return Math.max(-50, Math.min(50, offset))
})

/**
 * Calculate distance-based opacity for holographic effect
 * Opacity fades as mouse moves away from QR code center using quadratic falloff
 * - At center: MAX_HOLO_OPACITY (0.4) for visible effect without obscuring QR code
 * - At edge: MIN_HOLO_OPACITY (0) for transparent edges
 * - Respects reduced motion preference for accessibility
 */
const holoOpacity = computed(() => {
  // Disable holographic effect if user prefers reduced motion
  if (isInReducedMotionMode.value) {
    return MIN_HOLO_OPACITY
  }

  if (!containerRef.value) return MIN_HOLO_OPACITY
  if (!isHovering.value) return MIN_HOLO_OPACITY

  // Calculate Euclidean distance from mouse to QR code center (in percentage coordinates)
  const dx = gradientXPercent.value - CENTER_PERCENTAGE
  const dy = gradientYPercent.value - CENTER_PERCENTAGE
  const distance = Math.sqrt(dx * dx + dy * dy)

  // Maximum possible distance from center to corner in percentage space
  // sqrt(50² + 50²) ≈ 70.7%
  const maxDistance = Math.sqrt(CENTER_PERCENTAGE ** 2 + CENTER_PERCENTAGE ** 2)
  const normalizedDistance = Math.min(distance / maxDistance, 1) // Clamp to [0, 1]

  // Apply quadratic falloff curve for smooth, natural-looking fade
  // Formula: minOpacity + range * (1 - distance²)
  // This creates stronger effect near center that gradually fades toward edges
  const opacityRange = MAX_HOLO_OPACITY - MIN_HOLO_OPACITY
  const opacity = MIN_HOLO_OPACITY + opacityRange * (1 - normalizedDistance ** 2)

  return Math.max(MIN_HOLO_OPACITY, Math.min(MAX_HOLO_OPACITY, opacity))
})

//#endregion
</script>

<script lang="ts">
export enum CertificateQRCodeSizePreset {
  Display = 'display',
  Print = 'print',
}

export default {}
</script>

<template>
  <div
    ref="containerRef"
    :class="[
      'relative -rotate-[3.1deg]',
      sizePreset === CertificateQRCodeSizePreset.Display ? 'w-[56px] h-[72px]' : 'w-[78px] h-[100px]',
    ]"
    :style="{
      '--gradient-x': `${gradientXPercent}%`,
      '--gradient-y': `${gradientYPercent}%`,
      '--linear-angle': `${LINEAR_GRADIENT_ANGLE}deg`,
      '--gradient-initial-offset': `${gradientInitialOffset}%`,
    }"
  >
    <ImageThumbnail
      resize-strategy="fit"
      :original-image-width="57"
      :original-image-height="73"
      :process-image="true"
      src="https://padlet.net/dashboard/belt/v1_qr_code_ticket_base.png"
      alt=""
    />
    <div class="absolute top-1 start-1 end-1 fill-dark-text-100 opacity-80">
      <ImageThumbnail
        v-if="certificateQrCode"
        :aspect-ratio="1"
        resize-strategy="fit"
        :original-image-width="982"
        :original-image-height="982"
        :process-image="true"
        :src="certificateQrCode"
        alt=""
      />
    </div>
    <p
      :class="[
        'select-none pointer-events-none absolute',
        // position
        sizePreset === CertificateQRCodeSizePreset.Display ? 'my-0 bottom-0.5 start-1.5' : '-bottom-2.5 start-3',
        // font
        sizePreset === CertificateQRCodeSizePreset.Display ? 'qr-code-token-display' : 'qr-code-token-print',
      ]"
    >
      {{ qrCodeToken }}
    </p>
    <!-- CSS-based holo effect overlay (shiny metallic style) -->
    <div
      class="holo-overlay"
      :style="{
        opacity: holoOpacity,
        '--badge-color': badgeBackgroundProps.holoBadgeColor,
        '--holo-color-opacity': holoOpacity * 0.6,
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
      }"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@@/styles/syne_mono.scss';

.qr-code-token {
  font-family: 'Syne Mono', monospace;
  font-weight: 400;
  text-align: center;
  color: #000000;
  opacity: 0.9;
  mix-blend-mode: darken;
}

.qr-code-token-display {
  font-size: 10px;
  line-height: 13.7px;
  letter-spacing: 4%;
  @extend .qr-code-token;
}

.qr-code-token-print {
  font-size: 12px;
  line-height: 19.2px;
  letter-spacing: 4%;
  @extend .qr-code-token;
}

/**
 * Holographic overlay effect for QR codes
 *
 * Creates a multi-layered prismatic shine effect using stacked CSS gradients:
 * 1. Top layer: Iridescent color burst at cursor position (blues, yellows, greens, magentas)
 * 2. Second layer: Bright white spotlight following cursor for metallic highlight
 * 3. Third layer: Diagonal linear gradient streaks (45°) with shifting offset based on cursor position
 * 4. Base layer: Subtle radial shine from center for ambient metallic glow
 *
 * The effect responds to mouse movement via CSS custom properties:
 * - --gradient-x, --gradient-y: Cursor position in percentage for radial gradients
 * - --linear-angle: Fixed 45° angle for diagonal gradient direction
 * - --gradient-initial-offset: Dynamic offset (-100% to +100%) for shifting diagonal streaks
 * - --badge-color: Belt-specific color with opacity for themed gradient accents
 * - --holo-color-opacity: Dynamic opacity based on distance from center
 *
 * Note: Opacity is kept lower than badge effect (max 0.4) to not obscure QR code scanning
 */
.holo-overlay {
  position: absolute;
  inset: 0;
  border-radius: 4px;
  pointer-events: none;
  transition: all 0.3s ease-out;
  /* Performance optimizations for GPU acceleration */
  contain: layout style paint; /* Isolate rendering scope */
  transform: translateZ(0); /* Force GPU compositing layer */
  will-change: opacity; /* Hint browser for opacity changes */

  background:
    /* Layer 1: Iridescent color burst at cursor position
       Creates rainbow shimmer effect that follows the mouse
       Color sequence: cool blue → warm yellow → fresh green → magenta → cool lavender */
    radial-gradient(
      circle at var(--gradient-x) var(--gradient-y),
      rgba(200, 220, 255, 0.8) 0%,
      rgba(255, 240, 200, 0.7) 10.7%,
      rgba(200, 255, 220, 0.6) 21.4%,
      rgba(255, 200, 255, 0.55) 32.1%,
      rgba(220, 200, 255, 0.5) 42.8%,
      transparent 50%
    ),
    /* Layer 2: Bright white spotlight at cursor position
       Creates metallic highlight that emphasizes the holographic effect */
      radial-gradient(
        circle at var(--gradient-x) var(--gradient-y),
        rgba(255, 255, 255, 1) 0%,
        /* Bright core */ rgba(255, 255, 255, 0.8) 10%,
        /* Strong inner glow */ rgba(255, 255, 255, 0.6) 20%,
        /* Medium glow */ rgba(255, 255, 255, 0.48) 30%,
        /* Soft glow */ rgba(255, 255, 255, 0.2) 40%,
        /* Subtle glow */ transparent 60% /* Fade to transparent */
      ),
    /* Layer 3: Diagonal linear gradient streaks (45° angle)
       Creates parallel streaks of color running diagonally across QR code
       Pattern extends 0-200% (double size) to allow smooth panning via --gradient-initial-offset
       Offset shifts from -100% (top-left) to +100% (bottom-right) as cursor moves diagonally
       Pattern repeats: white → badge tint → white → badge tint → color accent → badge tint → ...
       Color accents: yellow (gold), green (lime), blue (cyan) spread throughout the pattern */
      linear-gradient(
        var(--linear-angle),
        /* First cycle: 0-16.67% (white → tinted → white) */ rgba(255, 255, 255, 0.8)
          calc(0% + var(--gradient-initial-offset)),
        var(--badge-color) calc(8.33% + var(--gradient-initial-offset)),
        rgba(255, 255, 255, 0.8) calc(16.67% + var(--gradient-initial-offset)),
        /* Second cycle: 25-41.67% (tinted → yellow accent → white) */ var(--badge-color)
          calc(25% + var(--gradient-initial-offset)),
        rgba(255, 212, 0, var(--holo-color-opacity)) calc(29.17% + var(--gradient-initial-offset)),
        /* Gold yellow */ var(--badge-color) calc(33.33% + var(--gradient-initial-offset)),
        rgba(255, 255, 255, 0.8) calc(41.67% + var(--gradient-initial-offset)),
        /* Third cycle: 50-66.67% (tinted → green accent → white) */ var(--badge-color)
          calc(50% + var(--gradient-initial-offset)),
        rgba(106, 255, 0, var(--holo-color-opacity)) calc(54.17% + var(--gradient-initial-offset)),
        /* Lime green */ var(--badge-color) calc(58.33% + var(--gradient-initial-offset)),
        rgba(255, 255, 255, 0.8) calc(66.67% + var(--gradient-initial-offset)),
        /* Fourth cycle: 75-100% (tinted → white → tinted → blue accent → tinted) */ var(--badge-color)
          calc(75% + var(--gradient-initial-offset)),
        rgba(255, 255, 255, 0.8) calc(83.33% + var(--gradient-initial-offset)),
        var(--badge-color) calc(91.67% + var(--gradient-initial-offset)),
        rgba(0, 157, 255, var(--holo-color-opacity)) calc(95.83% + var(--gradient-initial-offset)),
        /* Cyan blue */ var(--badge-color) calc(100% + var(--gradient-initial-offset)),
        /* Second half: 108.33-200% (repeats pattern for smooth wrapping) */ rgba(255, 255, 255, 0.8)
          calc(108.33% + var(--gradient-initial-offset)),
        var(--badge-color) calc(116.67% + var(--gradient-initial-offset)),
        rgba(255, 255, 255, 0.8) calc(125% + var(--gradient-initial-offset)),
        var(--badge-color) calc(133.33% + var(--gradient-initial-offset)),
        rgba(255, 212, 0, var(--holo-color-opacity)) calc(137.5% + var(--gradient-initial-offset)),
        var(--badge-color) calc(141.67% + var(--gradient-initial-offset)),
        rgba(255, 255, 255, 0.8) calc(150% + var(--gradient-initial-offset)),
        var(--badge-color) calc(158.33% + var(--gradient-initial-offset)),
        rgba(106, 255, 0, var(--holo-color-opacity)) calc(162.5% + var(--gradient-initial-offset)),
        var(--badge-color) calc(166.67% + var(--gradient-initial-offset)),
        rgba(255, 255, 255, 0.8) calc(175% + var(--gradient-initial-offset)),
        var(--badge-color) calc(183.33% + var(--gradient-initial-offset)),
        rgba(255, 255, 255, 0.8) calc(191.67% + var(--gradient-initial-offset)),
        var(--badge-color) calc(200% + var(--gradient-initial-offset))
      );
}
</style>
